/*
 * Make My Day header menus.
 *
 * - <mm-mega-menus>: desktop mega menus. Opens the panel attached to a header link (`[data-mega-menu-trigger]` +
 *   `aria-controls`) on hover, on click/tap or from the keyboard, and keeps it open while the pointer travels between
 *   the link and the panel.
 * - <mm-mobile-menu>: mobile menu sheet dropping from the header. Shows the menu links, then the mega menu panel (the
 *   same DOM as desktop) or nested link list of the chosen link, with a back button.
 * - <mm-mega-carousel>: swipeable row with previous/next arrows, used by groups set to "Carousel" on mobile.
 */
(() => {
  const DESKTOP_QUERY = 'screen and (min-width: 1200px)';
  const MOBILE_QUERY = 'screen and (max-width: 1199px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const CLOSE_DELAY = 200;
  const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const afterNextPaint = (callback) => requestAnimationFrame(() => requestAnimationFrame(callback));
  const isRendered = (element) => element.getClientRects().length > 0;
  const focusableIn = (root) => Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isRendered);

  /* ---------------------------------------------------------------------------------------------------------------- */
  /* Desktop mega menus                                                                                               */
  /* ---------------------------------------------------------------------------------------------------------------- */

  class MmMegaMenus extends HTMLElement {
    #abortController = null;
    #headerObserver = null;
    #closeTimer = null;
    #activePanel = null;
    #activeTrigger = null;
    #pinnedPanel = null;
    #lastPointerType = 'mouse';

    connectedCallback() {
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.header = this.closest('x-header') || document.body;
      this.bar = this.closest('.mm-header__bar') || this.header;
      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);

      this.#triggers.forEach((trigger) => {
        const item = trigger.closest('.mm-header__nav-item') || trigger;

        item.addEventListener('pointerenter', (event) => this.#onTriggerPointerEnter(event, trigger), { signal });
        item.addEventListener('pointerleave', (event) => this.#onPointerLeave(event), { signal });
        trigger.addEventListener('pointerdown', (event) => { this.#lastPointerType = event.pointerType; }, { signal });
        trigger.addEventListener('click', (event) => this.#onTriggerClick(event, trigger), { signal });
        trigger.addEventListener('keydown', (event) => this.#onTriggerKeydown(event, trigger), { signal });
      });

      this.#panels.forEach((panel) => {
        panel.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') {
            this.#cancelClose();
          }
        }, { signal });
        panel.addEventListener('pointerleave', (event) => this.#onPointerLeave(event), { signal });
        panel.addEventListener('keydown', (event) => this.#onPanelKeydown(event, panel), { signal });
      });

      document.addEventListener('keydown', (event) => this.#onDocumentKeydown(event), { signal });
      document.addEventListener('pointerdown', (event) => this.#onDocumentPointerDown(event), { signal });
      document.addEventListener('shopify:block:select', (event) => this.#onBlockSelect(event), { signal });
      document.addEventListener('shopify:block:deselect', (event) => this.#onBlockDeselect(event), { signal });
      this.header.addEventListener('focusout', (event) => this.#onFocusOut(event), { signal });
      this.desktopQuery.addEventListener('change', () => this.close(), { signal });

      // The theme hides the header on scroll (`is-hidden`): never leave a panel floating without its header
      this.#headerObserver = new MutationObserver(() => {
        if (this.header.classList.contains('is-hidden')) {
          this.close();
        }
      });
      this.#headerObserver.observe(this.header, { attributes: true, attributeFilter: ['class'] });

      if (window.Shopify?.designMode) {
        this.#flagDuplicatePanels();
      }
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#headerObserver?.disconnect();
      this.#cancelClose();
    }

    get openTrigger() {
      return this.getAttribute('trigger') === 'click' ? 'click' : 'hover';
    }

    get #triggers() {
      return Array.from(this.header.querySelectorAll('[data-mega-menu-trigger]')).filter((trigger) => this.#panelFor(trigger));
    }

    get #panels() {
      return Array.from(this.querySelectorAll('[data-mega-menu]'));
    }

    open(panel, { trigger = this.#triggerFor(panel), instant = false } = {}) {
      this.#cancelClose();

      if (!panel || this.#activePanel === panel || !this.desktopQuery.matches) {
        return;
      }

      if (this.#isEmpty(panel) && !window.Shopify?.designMode) {
        return;
      }

      const isSwitching = Boolean(this.#activePanel);

      if (isSwitching) {
        this.#hide(this.#activePanel, this.#activeTrigger, true);
      }

      this.#activePanel = panel;
      this.#activeTrigger = trigger;
      this.#show(panel, trigger, isSwitching || instant);
      this.header.classList.add('is-mega-open');
    }

    close({ returnFocus = false } = {}) {
      this.#cancelClose();

      if (!this.#activePanel) {
        return;
      }

      const trigger = this.#activeTrigger;

      this.#hide(this.#activePanel, trigger, false);
      this.#activePanel = null;
      this.#activeTrigger = null;
      this.#pinnedPanel = null;
      this.header.classList.remove('is-mega-open');

      if (returnFocus) {
        trigger?.focus();
      }
    }

    #show(panel, trigger, instant) {
      panel.classList.toggle('is-instant', instant);
      panel.classList.add('is-open');
      trigger?.setAttribute('aria-expanded', 'true');
      trigger?.classList.add('is-mega-open');

      if (instant) {
        afterNextPaint(() => panel.classList.remove('is-instant'));
      }
    }

    #hide(panel, trigger, instant) {
      panel.classList.toggle('is-instant', instant);
      panel.classList.remove('is-open');
      trigger?.setAttribute('aria-expanded', 'false');
      trigger?.classList.remove('is-mega-open');

      if (instant) {
        afterNextPaint(() => panel.classList.remove('is-instant'));
      }
    }

    #scheduleClose() {
      this.#cancelClose();
      this.#closeTimer = setTimeout(() => this.close(), CLOSE_DELAY);
    }

    #cancelClose() {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }

    #onTriggerPointerEnter(event, trigger) {
      if (event.pointerType !== 'mouse' || this.openTrigger !== 'hover' || this.#pinnedPanel || !this.desktopQuery.matches) {
        return;
      }

      this.open(this.#panelFor(trigger), { trigger });
    }

    #onPointerLeave(event) {
      if (event.pointerType !== 'mouse' || this.openTrigger !== 'hover' || this.#pinnedPanel || !this.#activePanel) {
        return;
      }

      this.#scheduleClose();
    }

    #onTriggerClick(event, trigger) {
      const pointerType = this.#lastPointerType;
      this.#lastPointerType = 'mouse';

      if (!this.desktopQuery.matches) {
        return;
      }

      // With a mouse in hover mode the panel is already open, so the click simply follows the link
      if (this.openTrigger === 'hover' && pointerType === 'mouse') {
        return;
      }

      // Touch, or click mode: the first click opens the panel, the next one follows the link
      const panel = this.#panelFor(trigger);

      if (this.#activePanel !== panel && !this.#isEmpty(panel)) {
        event.preventDefault();
        this.open(panel, { trigger });
      }
    }

    #onTriggerKeydown(event, trigger) {
      const panel = this.#panelFor(trigger);

      if (event.key === 'ArrowDown' || event.key === ' ') {
        event.preventDefault();
        this.open(panel, { trigger, instant: true });
        focusableIn(panel)[0]?.focus();
      } else if (event.key === 'Tab' && !event.shiftKey && this.#activePanel === panel) {
        const firstFocusable = focusableIn(panel)[0];

        if (firstFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }
      }
    }

    #onPanelKeydown(event, panel) {
      if (event.key !== 'Tab' || !this.desktopQuery.matches) {
        return;
      }

      const focusable = focusableIn(panel);
      const trigger = this.#triggerFor(panel);

      if (focusable.length === 0 || !trigger) {
        return;
      }

      if (event.shiftKey && document.activeElement === focusable[0]) {
        event.preventDefault();
        trigger.focus();
      } else if (!event.shiftKey && document.activeElement === focusable[focusable.length - 1]) {
        const nextFocusable = this.#focusableAfter(trigger);

        if (nextFocusable) {
          event.preventDefault();
          this.close();
          nextFocusable.focus();
        } else {
          this.close();
        }
      }
    }

    #onDocumentKeydown(event) {
      if (event.key !== 'Escape' || !this.#activePanel) {
        return;
      }

      const hadFocus = this.#activePanel.contains(document.activeElement) || document.activeElement === this.#activeTrigger;
      this.close({ returnFocus: hadFocus });
    }

    #onDocumentPointerDown(event) {
      if (!this.#activePanel || this.#pinnedPanel) {
        return;
      }

      if (this.#activePanel.contains(event.target) || this.#activeTrigger?.contains(event.target)) {
        return;
      }

      this.close();
    }

    #onFocusOut(event) {
      const nextTarget = event.relatedTarget;

      if (!this.#activePanel || this.#pinnedPanel || !nextTarget) {
        return;
      }

      if (this.#activePanel.contains(nextTarget) || this.#activeTrigger?.contains(nextTarget)) {
        return;
      }

      this.close();
    }

    // Theme editor: selecting a mega menu block (or anything inside it) reveals its panel and keeps it open
    #onBlockSelect(event) {
      const panel = event.target instanceof Element ? event.target.closest('[data-mega-menu]') : null;

      if (!panel || !this.contains(panel) || !this.desktopQuery.matches) {
        return;
      }

      this.open(panel, { instant: true });
      this.#pinnedPanel = panel;
    }

    #onBlockDeselect(event) {
      const panel = event.target instanceof Element ? event.target.closest('[data-mega-menu]') : null;

      if (!panel || panel !== this.#pinnedPanel) {
        return;
      }

      this.#pinnedPanel = null;
      this.close();
    }

    // Theme editor only: the header attaches the first panel targeting a link, so warn on the other ones
    #flagDuplicatePanels() {
      const seenTargets = new Set();

      this.#panels.forEach((panel) => {
        const target = (panel.getAttribute('data-mega-target') || '').split('::')[0];
        const notice = panel.querySelector('[data-mega-duplicate-notice]');

        if (!target) {
          return;
        }

        if (notice) {
          notice.hidden = !seenTargets.has(target);
        }

        seenTargets.add(target);
      });
    }

    #panelFor(trigger) {
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      return panel && this.contains(panel) ? panel : null;
    }

    #triggerFor(panel) {
      if (!panel) {
        return null;
      }

      return Array.from(this.header.querySelectorAll('[data-mega-menu-trigger]')).find((trigger) => trigger.getAttribute('aria-controls') === panel.id) || null;
    }

    #isEmpty(panel) {
      const inner = panel?.querySelector('.mm-mega__inner');
      return !inner || !Array.from(inner.children).some((child) => !child.classList.contains('mm-mega__notice'));
    }

    #focusableAfter(trigger) {
      const focusable = focusableIn(this.bar).filter((element) => !this.contains(element) && !element.closest('mm-mobile-menu'));
      const index = focusable.indexOf(trigger);

      return index === -1 ? null : focusable[index + 1] || null;
    }
  }

  /* ---------------------------------------------------------------------------------------------------------------- */
  /* Mobile menu                                                                                                      */
  /* ---------------------------------------------------------------------------------------------------------------- */

  class MmMobileMenu extends HTMLElement {
    #abortController = null;
    #isOpen = false;
    #isPinned = false;
    #activeButton = null;
    #resetTimer = null;
    #heightAnimation = null;

    connectedCallback() {
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.header = this.closest('x-header') || document.body;
      this.bar = this.closest('.mm-header__bar') || this.header;
      this.sheet = this.querySelector('.mm-mobile-menu__sheet');
      this.scroller = this.querySelector('.mm-mobile-menu__scroller');
      this.backButton = this.querySelector('[data-mobile-menu-back]');
      this.backLabel = this.querySelector('[data-mobile-menu-back-label]');
      this.viewAllLink = this.querySelector('[data-mobile-menu-view-all]');
      this.mobileQuery = window.matchMedia(MOBILE_QUERY);
      this.reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);

      this.#toggles.forEach((toggle) => {
        toggle.addEventListener('click', (event) => {
          event.preventDefault();

          if (this.#isOpen) {
            this.close({ returnFocus: true });
          } else {
            this.open();
          }
        }, { signal });
      });

      this.#panelButtons.forEach((button) => button.addEventListener('click', () => this.showPanel(button), { signal }));
      this.backButton?.addEventListener('click', () => this.showRoot(), { signal });
      this.querySelector('.mm-mobile-menu__overlay')?.addEventListener('click', () => this.close(), { signal });

      // Following a link from the menu (including same-page anchors) closes it
      this.sheet.addEventListener('click', (event) => {
        if (this.#isOpen && event.target instanceof Element && event.target.closest('a[href]')) {
          this.close();
        }
      }, { signal });

      // Using the other header controls (logo, account, cart...) while the menu is open closes it first
      this.bar.addEventListener('click', (event) => this.#onBarClick(event), { signal });

      document.addEventListener('keydown', (event) => this.#onDocumentKeydown(event), { signal });
      document.addEventListener('shopify:block:select', (event) => this.#onBlockSelect(event), { signal });
      document.addEventListener('shopify:block:deselect', (event) => this.#onBlockDeselect(event), { signal });
      window.addEventListener('resize', () => this.#isOpen && this.#updateOffset(), { signal, passive: true });

      // Crossing the breakpoint (resizing, rotating a tablet) swaps the desktop and mobile layouts of the sheet: skip the
      // transitions so the sheet never visibly slides away
      this.mobileQuery.addEventListener('change', () => {
        this.close({ immediate: true });
        this.classList.add('is-instant');
        afterNextPaint(() => {
          if (!this.#isOpen) {
            this.classList.remove('is-instant');
          }
        });
      }, { signal });
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#heightAnimation?.cancel();
      clearTimeout(this.#resetTimer);

      if (this.#isOpen) {
        document.documentElement.classList.remove('mm-mobile-menu-open');
      }
    }

    get isOpen() {
      return this.#isOpen;
    }

    get #toggles() {
      return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
    }

    get #panelButtons() {
      return Array.from(this.querySelectorAll('[data-mobile-panel-target]'));
    }

    get #panels() {
      return Array.from(this.querySelectorAll('[data-mega-menu], [data-mobile-panel]'));
    }

    open({ immediate = false } = {}) {
      if (this.#isOpen || !this.mobileQuery.matches) {
        return;
      }

      clearTimeout(this.#resetTimer);
      this.#isOpen = true;
      this.#updateOffset();
      this.#setView(null);

      this.classList.toggle('is-instant', immediate);
      this.classList.add('is-open');
      this.sheet.setAttribute('role', 'dialog');
      this.sheet.setAttribute('aria-modal', 'true');
      this.#toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'true'));
      this.header.classList.add('is-mobile-menu-open');
      document.documentElement.classList.add('mm-mobile-menu-open');

      if (immediate) {
        afterNextPaint(() => this.classList.remove('is-instant'));
      }
    }

    close({ immediate = false, returnFocus = false } = {}) {
      if (!this.#isOpen) {
        return;
      }

      const hadFocus = this.contains(document.activeElement);

      this.#isOpen = false;
      this.#isPinned = false;
      this.#heightAnimation?.cancel();

      this.classList.toggle('is-instant', immediate);
      this.classList.remove('is-open');
      this.sheet.removeAttribute('role');
      this.sheet.removeAttribute('aria-modal');
      this.#toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
      this.header.classList.remove('is-mobile-menu-open');
      document.documentElement.classList.remove('mm-mobile-menu-open');

      if (returnFocus || hadFocus) {
        this.#toggles.find(isRendered)?.focus({ preventScroll: true });
      }

      // Go back to the first level once the sheet is out of view, so the menu always reopens on the menu links
      const reset = () => {
        if (!this.#isOpen) {
          this.#setView(null);
        }

        this.classList.remove('is-instant');
      };

      clearTimeout(this.#resetTimer);

      if (immediate) {
        afterNextPaint(reset);
      } else {
        this.#resetTimer = setTimeout(reset, 500);
      }
    }

    showPanel(target, { animate = true } = {}) {
      const button = target?.hasAttribute('data-mobile-panel-target') ? target : null;
      const panel = button ? document.getElementById(button.getAttribute('data-mobile-panel-target')) : target;

      if (!panel || !this.contains(panel)) {
        return;
      }

      this.#animateHeight(() => this.#setView(panel, button), animate);
      this.scroller.scrollTop = 0;

      if (animate) {
        this.backButton?.focus({ preventScroll: true });
      }
    }

    showRoot({ animate = true } = {}) {
      const previousButton = this.#activeButton;

      this.#animateHeight(() => this.#setView(null), animate);
      this.scroller.scrollTop = 0;

      if (animate) {
        previousButton?.focus({ preventScroll: true });
      }
    }

    #setView(panel, button = null) {
      this.#panels.forEach((item) => item.classList.toggle('is-mobile-active', item === panel));
      this.#panelButtons.forEach((item) => item.setAttribute('aria-expanded', String(item === button)));
      this.classList.toggle('is-panel-view', Boolean(panel));
      this.#activeButton = button;

      if (!panel) {
        return;
      }

      const title = button?.querySelector('.mm-mobile-menu__link-text')?.textContent.trim()
        || (panel.getAttribute('data-mega-target') || '').split('::')[0].replace(/-/g, ' ');

      if (this.backLabel) {
        this.backLabel.textContent = title;
      }

      if (this.viewAllLink) {
        const url = button?.getAttribute('data-mobile-panel-url') || '';
        const isUsable = url !== '' && url !== '#';

        this.viewAllLink.hidden = !isUsable;

        if (isUsable) {
          this.viewAllLink.setAttribute('href', url);
        }
      }
    }

    // Smoothly animates the sheet between the height of the current view and the next one
    #animateHeight(update, animate) {
      if (!animate || !this.#isOpen || this.reducedMotionQuery.matches) {
        update();
        return;
      }

      const fromHeight = this.sheet.getBoundingClientRect().height;
      update();
      const toHeight = this.sheet.getBoundingClientRect().height;

      this.#heightAnimation?.cancel();

      if (Math.abs(fromHeight - toHeight) < 1) {
        return;
      }

      this.#heightAnimation = this.sheet.animate(
        [{ height: `${fromHeight}px` }, { height: `${toHeight}px` }],
        { duration: 450, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
      );
    }

    #updateOffset() {
      const barBottom = this.bar.getBoundingClientRect().bottom;
      this.style.setProperty('--mm-mobile-menu-offset', `${Math.max(0, Math.round(barBottom))}px`);
    }

    #onBarClick(event) {
      if (!this.#isOpen || !(event.target instanceof Element)) {
        return;
      }

      const control = event.target.closest('a[href], button');

      if (!control || this.contains(control) || this.#toggles.includes(control)) {
        return;
      }

      this.close({ immediate: true });
    }

    #onDocumentKeydown(event) {
      if (!this.#isOpen) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close({ returnFocus: true });
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      // Keep the focus inside the menu: the header toggle (to close it) and the visible menu content
      const focusable = [...this.#toggles.filter(isRendered), ...focusableIn(this.sheet)];

      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!focusable.includes(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // Theme editor: selecting a mega menu block on a mobile preview opens the menu on its panel
    #onBlockSelect(event) {
      if (!this.mobileQuery.matches || !(event.target instanceof Element) || !this.contains(event.target)) {
        return;
      }

      const panel = event.target.closest('[data-mega-menu]');

      if (!panel) {
        return;
      }

      const button = this.#panelButtons.find((item) => item.getAttribute('data-mobile-panel-target') === panel.id);

      this.open({ immediate: true });
      this.showPanel(button || panel, { animate: false });
      this.#isPinned = true;
    }

    #onBlockDeselect(event) {
      if (!this.#isPinned || !(event.target instanceof Element) || !this.contains(event.target)) {
        return;
      }

      this.close({ immediate: true });
    }
  }

  /* ---------------------------------------------------------------------------------------------------------------- */
  /* Carousel                                                                                                         */
  /* ---------------------------------------------------------------------------------------------------------------- */

  class MmMegaCarousel extends HTMLElement {
    #abortController = null;
    #resizeObserver = null;
    #frame = null;

    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');

      if (!this.track || !this.prevButton || !this.nextButton) {
        return;
      }

      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.prevButton.addEventListener('click', () => this.#go(-1), { signal });
      this.nextButton.addEventListener('click', () => this.#go(1), { signal });
      this.track.addEventListener('scroll', () => this.#scheduleUpdate(), { signal, passive: true });

      // Theme editor: bring the selected slide into view
      this.addEventListener('shopify:block:select', (event) => {
        const slide = Array.from(this.track.children).find((child) => child.contains(event.target));

        if (slide) {
          this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft });
        }
      }, { signal });

      // Also catches the panel becoming visible (it is `display: none` until opened)
      this.#resizeObserver = new ResizeObserver(() => this.#scheduleUpdate());
      this.#resizeObserver.observe(this.track);
      this.#update();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#resizeObserver?.disconnect();
      cancelAnimationFrame(this.#frame);
    }

    #go(direction) {
      const firstSlide = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = firstSlide ? firstSlide.getBoundingClientRect().width + gap : this.track.clientWidth;
      const behavior = window.matchMedia(REDUCED_MOTION_QUERY).matches ? 'auto' : 'smooth';

      this.track.scrollBy({ left: direction * step, behavior });
    }

    #scheduleUpdate() {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#update());
    }

    #update() {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const isScrollable = maxScroll > 1 && getComputedStyle(this.track).overflowX !== 'visible';
      const position = Math.abs(this.track.scrollLeft);

      this.classList.toggle('is-scrollable', isScrollable);
      this.prevButton.disabled = !isScrollable || position <= 1;
      this.nextButton.disabled = !isScrollable || position >= maxScroll - 1;
    }
  }

  const define = (name, constructor) => {
    if (!window.customElements.get(name)) {
      window.customElements.define(name, constructor);
    }
  };

  define('mm-mega-menus', MmMegaMenus);
  define('mm-mobile-menu', MmMobileMenu);
  define('mm-mega-carousel', MmMegaCarousel);
})();
